/**
 * Per-section schedule drawer (SC-1; docs/scheduled-content-contracts.md §1).
 *
 * Two `datetime-local` bounds — "show from" and "hide from" — resolved to
 * epoch ms in THIS browser's timezone (the disclosed v1 semantics), committed
 * as ONE `store.update` on the section's `schedule` path so it is a single
 * undo step and an ordinary collab patch. Clear writes `null`; normalization
 * strips it everywhere that hashes (portable/init), so clearing never moves a
 * schedule-less project's contentHash.
 *
 * The canvas ALWAYS renders scheduled sections — an editor must see what they
 * are editing; the summary sentence is the indication. Single-section only in
 * v1 (a multi-selection hides the row).
 */

import { store } from '../../core/state.js';
import { createButton } from '../components.js';
import { sanitizeSchedule, scheduleSummary } from '../../shared/schedule-state.js';

export const SCHEDULE_DRAWER_FLAG = 'pb-scheduled-content';

/** The escape hatch: `localStorage['pb-scheduled-content'] = '0'` hides it. */
export function scheduleDrawerEnabled() {
  try {
    return localStorage.getItem(SCHEDULE_DRAWER_FLAG) !== '0';
  } catch {
    return true;
  }
}

function sectionPathFor(state, instanceId) {
  for (let p = 0; p < (state.pages || []).length; p++) {
    const sections = state.pages[p].sections || [];
    for (let s = 0; s < sections.length; s++) {
      if (sections[s]?.instanceId === instanceId) return `pages.${p}.sections.${s}`;
    }
  }
  return null;
}

function toLocalInputValue(ms) {
  if (ms == null) return '';
  const d = new Date(ms);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function fromLocalInputValue(value) {
  if (!value) return null;
  const ms = new Date(value).getTime();
  return Number.isFinite(ms) ? ms : null;
}

export function renderScheduleDrawer(container, ctx = {}) {
  if (!container) return () => {};
  let validationError = '';

  const build = () => {
    container.replaceChildren();
    const state = store.getState();
    const path = sectionPathFor(state, ctx.instanceId);
    const section = path ? path.split('.').reduce((acc, key) => acc?.[key], state) : null;
    if (!section) return;
    const current = sanitizeSchedule(section.schedule) || { showAt: null, hideAt: null };

    const intro = document.createElement('p');
    intro.className = 'pb-props-hint';
    intro.textContent =
      'The section stays visible in the editor. On the live site it shows only inside ' +
      'this window — enforced on PageStitch-hosted sites, and by a small script (with ' +
      'the content still in the page) everywhere else.';
    container.appendChild(intro);

    const commit = (nextShow, nextHide, label) => {
      const clean = sanitizeSchedule({ showAt: nextShow, hideAt: nextHide });
      if ((nextShow !== null || nextHide !== null) && !clean) {
        validationError = 'That window is not valid — "show" must come before "hide".';
        build();
        return;
      }
      validationError = '';
      store.update(`${path}.schedule`, clean, label);
      build();
    };

    const makeField = (labelText, value, onCommit) => {
      const row = document.createElement('div');
      row.className = 'pb-override-row pb-schedule-row';
      const label = document.createElement('label');
      label.className = 'pb-props-label';
      label.textContent = labelText;
      const input = document.createElement('input');
      input.type = 'datetime-local';
      input.className = 'pb-input pb-schedule-input';
      input.value = toLocalInputValue(value);
      input.addEventListener('change', () => onCommit(fromLocalInputValue(input.value)));
      label.appendChild(input);
      row.appendChild(label);
      container.appendChild(row);
      return input;
    };

    makeField('Show from', current.showAt, (ms) => commit(ms, current.hideAt, 'Schedule section'));
    makeField('Hide from', current.hideAt, (ms) => commit(current.showAt, ms, 'Schedule section'));

    const status = document.createElement('p');
    status.className = 'pb-props-hint pb-schedule-status';
    status.setAttribute('role', 'status');
    const said = scheduleSummary(section.schedule);
    status.textContent = validationError
      ? validationError
      : said
        ? `Scheduled: ${said}.`
        : 'Not scheduled — the section always shows.';
    container.appendChild(status);

    if (section.schedule != null) {
      const clear = createButton({
        variant: 'secondary',
        label: 'Clear schedule',
        className: 'pb-schedule-clear',
        onClick: () => {
          validationError = '';
          store.update(`${path}.schedule`, null, 'Clear schedule');
          build();
        }
      });
      container.appendChild(clear);
    }
  };

  build();
  return () => {};
}

/** The inspector-row summary. Empty string = unscheduled (row shows a dash). */
export function renderScheduleSummary(section) {
  return scheduleSummary(section?.schedule);
}
